import React, { useRef, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  StatusBar,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  Image,
} from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../components/DirectionalIcon';
import { Input, Button, PhoneInput, CountryPicker, IndustryPicker } from '../components/design-system';
import { MapLocationPicker } from '../components/MapLocationPicker';
import { BusinessHoursForm, BusinessHoursValue, defaultBusinessHours, validateBusinessHours } from '../components/business/BusinessHoursForm';
import { DualPaneLayout, colors, spacing } from '../components';
import { designTokens } from '../theme/design-system';
import { useAppContext } from '../contexts/AppContext';
import { useAppStore } from '../store/appStore';
import { apiService } from '../services/api';
import { activateBusinessSession } from '../services/business-session';
import { parseInternationalPhoneNumber } from '../utils/phoneNumber';
import { handleApiError, logError, extractErrorEntryMessage } from '../utils/errorHandler';
import { useLocale } from '../contexts/LocaleContext';
import { useTypography } from '../hooks/useTypography';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useHardwareBackAction } from '../navigation/hardwareBack';

type WizardStep = 'details' | 'extras' | 'hours';
const STEPS: WizardStep[] = ['details', 'extras', 'hours'];

interface PhoneInputHandle {
  getValue: () => { nationalNumber: string; formattedNumber: string; e164Format: string };
  getCountryCode: () => string;
}

/**
 * Create a business in three short steps:
 *  1. details — name, industry, email (required), phone (optional, pre-filled
 *     from the user's verified number; no code — the old login-OTP check here
 *     created a second user account owning that number)
 *  2. extras — logo and location (map pin saved), all optional
 *  3. hours — timezone, currency, working days and hours, pre-filled from
 *     the country; editable later in Settings → Business profile
 * Then a proper business session (stored refresh token) and plan selection.
 */
export const BusinessCreationWizardScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { showNotification, navigateTo } = useAppContext();
  const { user, phase1Token, setCurrentBusiness } = useAppStore();
  const { t } = useLocale();
  const typo = useTypography();

  const [step, setStep] = useState<WizardStep>('details');
  const [isLoading, setIsLoading] = useState(false);

  const hasRealEmail = !!user?.email && !user.email.endsWith('@identity.sendifier.internal');
  const [form, setForm] = useState({
    name: '',
    email: hasRealEmail ? user!.email : '',
    industry: '',
    description: '',
    address: '',
    city: '',
    country: '',
  });
  const [location, setLocation] = useState<{ latitude: number; longitude: number; address: string } | null>(null);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [pickedImageUri, setPickedImageUri] = useState<string | null>(null);
  const phoneInputRef = useRef<PhoneInputHandle>(null);
  // Pre-filled from the user's verified mobile. PhoneInput takes the national
  // number plus the country (it adds the dial code itself) — passing the full
  // +20… number showed the code twice and saved it twice (+2020…).
  const [initialPhone] = useState(() => {
    if (!user?.mobile) return { national: '', country: undefined as string | undefined };
    const raw = String(user.mobile).startsWith('+') ? String(user.mobile) : `+${user.mobile}`;
    try {
      const parsed = parseInternationalPhoneNumber(raw);
      if (parsed?.nationalNumber) return { national: parsed.nationalNumber, country: parsed.country || undefined };
    } catch { /* fall through */ }
    return { national: '', country: undefined };
  });
  const [phone, setPhone] = useState(initialPhone.national);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Filled from the country when the hours step first opens; null until then.
  const [hours, setHours] = useState<BusinessHoursValue | null>(null);
  const goToHours = () => {
    if (!hours) setHours(defaultBusinessHours(form.country));
    setStep('hours');
  };

  const updateForm = (field: keyof typeof form, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const stepIndex = STEPS.indexOf(step);

  const validateDetails = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (form.name.trim().length < 2) newErrors.name = t('validation.required');
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email.trim())) newErrors.email = t('validation.emailInvalid');
    // 'other' alone means Other was picked but nothing typed yet
    if (!form.industry.trim() || form.industry === 'other') newErrors.industry = t('validation.required');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onContinueDetails = () => {
    if (validateDetails()) setStep('extras');
  };

  const onPickImage = async () => {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') {
      showNotification('warning', t('business.photoPermissionTitle'), t('business.photoPermissionBody'));
      return;
    }
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [1, 1],
      quality: 0.8,
    });
    if (!result.canceled && result.assets[0]) {
      setPickedImageUri(result.assets[0].uri);
    }
  };

  /** E.164 (+9665…) from the phone input, or '' when left empty. */
  const getBusinessPhone = (): string => {
    const data = phoneInputRef.current?.getValue();
    if (data?.nationalNumber) return data.e164Format || data.formattedNumber;
    return '';
  };

  const onCreateBusiness = async () => {
    if (!validateDetails()) {
      setStep('details');
      return;
    }
    const finalHours = hours || defaultBusinessHours(form.country);
    const hoursError = validateBusinessHours(finalHours);
    if (hoursError) {
      setHours(finalHours);
      setStep('hours');
      showNotification('error', t('businessHours.title'), t(hoursError as any));
      return;
    }
    setIsLoading(true);
    try {
      if (phase1Token) apiService.setAuthToken(phase1Token);
      const response = await apiService.createBusiness({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: getBusinessPhone(),
        industry: form.industry.trim(),
        description: form.description.trim(),
        address: location?.address || form.address.trim(),
        city: form.city.trim(),
        country: form.country.trim(),
        ...(location ? { location: { latitude: location.latitude, longitude: location.longitude } } : {}),
        timezone: finalHours.timezone,
        currency: finalHours.currency,
        workingHours: finalHours.workingHours,
      } as any);

      if (response.statusCode !== 1000 || !response.data) {
        showNotification('error', t('business.createFailed'), extractErrorEntryMessage(response.errors?.[0]) || t('billing.errorGeneric'));
        return;
      }

      const businessId = (response.data as any)._id;
      setCurrentBusiness(response.data as any);
      // Real session for the new business (stored refresh token) — the
      // create response's own token could not be refreshed.
      const ok = await activateBusinessSession(businessId);
      if (!ok) {
        showNotification('warning', t('business.createdTitle'), t('business.signInAgain'));
        navigateTo('business-picker');
        return;
      }

      if (pickedImageUri && businessId) {
        try {
          await apiService.uploadBusinessLogo(businessId, pickedImageUri, `logo-${businessId}.jpg`, 'image/jpeg');
        } catch (logoErr) {
          logError(logoErr, 'BusinessCreationWizardScreen:logo');
        }
      }

      navigateTo('plan-selection');
    } catch (err) {
      const { title, message, type } = handleApiError(err);
      showNotification(type, title, message);
    } finally {
      setIsLoading(false);
    }
  };

  const onBack = () => {
    if (stepIndex > 0) {
      setStep(STEPS[stepIndex - 1]);
    } else {
      navigateTo('business-picker');
    }
  };
  useHardwareBackAction(onBack);

  const renderHeader = () => (
    <View style={styles.header}>
      <TouchableOpacity onPress={onBack} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }} style={styles.backButton}>
        <DirectionalIcon name="chevron-back" size={22} color={colors.textDark} />
        <Text style={styles.backButtonText}>{t('common.back')}</Text>
      </TouchableOpacity>
      <View style={styles.stepIndicator}>
        {STEPS.map((s, idx) => (
          <View key={s} style={[styles.stepDot, idx <= stepIndex && styles.stepDotActive]} />
        ))}
      </View>
    </View>
  );

  const renderDetailsStep = () => (
    <View style={styles.stepContent}>
      <Text style={[styles.stepTitle, { fontFamily: typo.fontFamily.bold }]}>{t('business.details')}</Text>
      <Input label={t('business.name')} value={form.name} onChangeText={(v) => updateForm('name', v)} error={errors.name} variant="outlined" />
      <IndustryPicker label={t('business.industry')} value={form.industry} onChange={(v) => updateForm('industry', v)} error={errors.industry} />
      <Input
        label={t('business.email')}
        value={form.email}
        onChangeText={(v) => updateForm('email', v)}
        autoCapitalize="none"
        keyboardType="email-address"
        error={errors.email}
        variant="outlined"
        latin
      />
      <PhoneInput
        ref={phoneInputRef as any}
        label={t('business.phoneOptional')}
        value={phone}
        onChangeText={setPhone}
        initialCountry={initialPhone.country}
        variant="outlined"
      />
      <Button title={t('auth.continue')} onPress={onContinueDetails} variant="primary" size="lg" style={styles.stepButton} fullWidth />
    </View>
  );

  const renderExtrasStep = () => (
    <View style={styles.stepContent}>
      <Text style={[styles.stepTitle, { fontFamily: typo.fontFamily.bold }]}>{t('business.extrasTitle')}</Text>
      <Text style={styles.stepSubtitle}>{t('business.extrasSubtitle')}</Text>

      <TouchableOpacity style={styles.imagePicker} onPress={onPickImage} activeOpacity={0.85}>
        {pickedImageUri ? (
          <Image source={{ uri: pickedImageUri }} style={styles.pickedImage} />
        ) : (
          <>
            <Ionicons name="camera" size={28} color={colors.primary} />
            <Text style={styles.imagePickerText}>{t('business.addLogo')}</Text>
          </>
        )}
      </TouchableOpacity>

      <TouchableOpacity style={styles.locationPickerButton} onPress={() => setShowLocationPicker(true)} activeOpacity={0.85}>
        <Ionicons name="location" size={20} color={colors.primary} />
        <Text style={styles.locationPickerText} numberOfLines={2}>
          {location?.address || t('business.searchLocation')}
        </Text>
        <DirectionalIcon name="chevron-forward" size={18} color={colors.textLight} />
      </TouchableOpacity>
      <Input label={t('business.city')} value={form.city} onChangeText={(v) => updateForm('city', v)} variant="outlined" />
      <CountryPicker label={t('business.country')} value={form.country} onChange={(v) => updateForm('country', v)} />

      <Button title={t('auth.continue')} onPress={goToHours} variant="primary" size="lg" style={styles.stepButton} fullWidth />
      <TouchableOpacity onPress={goToHours}>
        <Text style={styles.skipText}>{t('businessHours.skipLocation')}</Text>
      </TouchableOpacity>

      <MapLocationPicker
        visible={showLocationPicker}
        onClose={() => setShowLocationPicker(false)}
        onLocationSelect={(loc) => {
          setLocation(loc);
          updateForm('address', loc.address);
          if (loc.city) updateForm('city', loc.city);
          if (loc.country) updateForm('country', loc.country);
          setShowLocationPicker(false);
        }}
        initialLocation={location}
      />
    </View>
  );

  const renderHoursStep = () => (
    <View style={styles.stepContent}>
      <Text style={[styles.stepTitle, { fontFamily: typo.fontFamily.bold }]}>{t('businessHours.title')}</Text>
      <Text style={styles.stepSubtitle}>{t('businessHours.subtitle')}</Text>
      {hours && <BusinessHoursForm value={hours} onChange={setHours} />}
      <Button title={t('business.createBusiness')} onPress={onCreateBusiness} variant="primary" size="lg" loading={isLoading} disabled={isLoading} style={styles.stepButton} fullWidth />
    </View>
  );

  return (
    <DualPaneLayout title="DISLIX" subtitle={t('business.createYourBusiness')}>
      <SafeAreaView style={styles.container}>
        <StatusBar barStyle="dark-content" backgroundColor={colors.white} />
        <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
            {renderHeader()}
            {step === 'details' && renderDetailsStep()}
            {step === 'extras' && renderExtrasStep()}
            {step === 'hours' && renderHoursStep()}
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </DualPaneLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.surface,
  },
  flex: {
    flex: 1,
  },
  content: {
    flexGrow: 1,
    paddingHorizontal: spacing.screenPadding,
    paddingTop: designTokens.spacing.xl,
    paddingBottom: designTokens.spacing.xl,
  },
  header: {
    marginBottom: designTokens.spacing.xl,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    marginBottom: designTokens.spacing.md,
  },
  backButtonText: {
    fontSize: 15,
    color: colors.textDark,
  },
  stepIndicator: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: designTokens.spacing.sm,
  },
  stepDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.border,
  },
  stepDotActive: {
    backgroundColor: colors.primary,
  },
  stepContent: {
    gap: designTokens.spacing.md,
  },
  stepSubtitle: {
    fontSize: 13,
    color: colors.textLight,
    marginTop: -spacing.sm,
    marginBottom: spacing.sm,
  },
  imagePickerText: {
    fontSize: 12,
    color: colors.primary,
    marginTop: 4,
  },
  stepTitle: {
    fontSize: 20,
    color: colors.textDark,
    marginBottom: designTokens.spacing.sm,
  },
  stepButton: {
    marginTop: designTokens.spacing.md,
  },
  imagePicker: {
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: colors.primaryTransparent,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    overflow: 'hidden',
  },
  pickedImage: {
    width: 120,
    height: 120,
  },
  skipText: {
    color: colors.textLight,
    textAlign: 'center',
    marginTop: designTokens.spacing.sm,
  },
  phoneRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: designTokens.spacing.sm,
  },
  phoneInputWrap: {
    flex: 1,
  },
  inlineActionButton: {
    marginBottom: 2, // aligns the button's bottom edge with PhoneInput's (which has a label above it)
  },
  otpRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: designTokens.spacing.sm,
  },
  verifiedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: designTokens.spacing.xs,
    marginBottom: 2,
  },
  verifiedText: {
    color: colors.primary,
    fontSize: 14,
  },
  locationPickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: designTokens.spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    paddingHorizontal: designTokens.spacing.md,
    paddingVertical: designTokens.spacing.md,
    backgroundColor: colors.background.surface,
  },
  locationPickerText: {
    flex: 1,
    color: colors.textDark,
    fontSize: 14,
  },
});
